import { Card as AntCard, Avatar, Empty, Spin, Tag, Timeline } from "antd";
import classNames from "classnames";
import { find, get, isEmpty, upperCase } from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  MdAccessTime,
  MdArrowForward,
  MdBarChart,
  MdCalendarToday,
  MdCheckCircle,
  MdEmail,
  MdFlashOn,
  MdFolder,
  MdLink,
  MdPerson,
  MdPhone,
} from "react-icons/md";
import Button from "shared/components/Button";
import { useLazyQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import { TRACER } from "utils/api/graphql/queries/tracers";
import {
  CONTACT_POINT_SYSTEM,
  CURRENCY_SYMBOL,
  DATE_STRING_FORMAT,
  TASK_STATUSES,
  THEMES_PALETTE,
} from "utils/constants";
import { displayDate } from "utils/helpers/date";
import { costToFrench } from "utils/helpers/values";
import { TASK_STATUS_ICON } from "../View";
import { STATES } from "./TaskDetailsDrawer";

const TaskDetails = ({
  task,
  setOpenTerminate,
  userActivities,
  userActivitiesLoading,
  subscriptions,
  onNavigateProjectDetail,
  project,
  setOpenContactCard,
  taskStatus,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.TASK);
  const DETAIL = t("DETAIL", { returnObjects: true });

  const [getTracer] = useLazyQuery(TRACER);

  return (
    <div className="task-details-container">
      <div className="left-panel">
        <div className="header">
          <div className="header__title-section">
            <h1>
              {get(task, "campaignStep.name", "")}
              <MdFlashOn className="icon-yellow" />
            </h1>
            {/* <MdEdit className="edit-icon" /> */}
          </div>
          <p className="header__description">{DETAIL.DESCRIPTION}</p>
        </div>

        <Button
          type="success"
          className="complete-button"
          icon={<MdCheckCircle />}
          size="large"
          block
          onClick={() => setOpenTerminate({ open: true })}
          disabled={taskStatus === TASK_STATUSES.COMPLETED}
        >
          {taskStatus === TASK_STATUSES.COMPLETED
            ? DETAIL.TASK_COMPLETED
            : DETAIL.MARK_AS_COMPLETE}
        </Button>

        <div className="properties-section">
          <h3>{DETAIL.TASK_PROPERTIES}</h3>

          <div className="properties-section__row">
            <div className="properties-section__label">
              <MdAccessTime />
              <span>{DETAIL.STATUS}</span>
            </div>
            <div className="properties-section__value">
              {/* <MdAccessTime style={{ color: COLORS.C_PRIMARY }} /> */}
              <div
                className={classNames("task-list__tag", {
                  "task-list__tag--success":
                    get(task, "status") === TASK_STATUSES.COMPLETED,
                })}
              >
                {TASK_STATUS_ICON[get(task, "status")]}{" "}
                {DETAIL.STATUSES[get(task, "status")] || get(task, "status")}
              </div>
            </div>
          </div>

          <div className="properties-section__row">
            <div className="properties-section__label">
              <MdCalendarToday />
              <span>{DETAIL.DUE_DATE}</span>
            </div>
            <div className="properties-section__value">
              {displayDate({ date: get(task, "event.startDate") })}
              {/* <MdEdit className="edit-small" /> */}
            </div>
          </div>

          <div className="properties-section__row">
            <div className="properties-section__label">
              <MdPerson />
              <span>{DETAIL.ASSIGNED_TO}</span>
            </div>
            <div className="properties-section__value">
              <Avatar
                size="small"
                style={{ backgroundColor: COLORS.C_SUCCESS }}
              >
                {upperCase(
                  `${
                    get(task, "project.author.firstname")?.substring(0, 1) || ""
                  } ${
                    get(task, "project.author.lastname")?.substring(0, 1) || ""
                  }`,
                )}
              </Avatar>
              {`${get(task, "project.author.firstname") || ""} ${
                get(task, "project.author.lastname") || ""
              }`}
            </div>
          </div>

          {/* <div className="properties-section__row">
            <div className="properties-section__label">
              <MdPerson />
              <span>{DETAIL.CREATED_BY}</span>
            </div>
            <div className="properties-section__value">
              <Avatar size="small" style={{ backgroundColor: COLORS.C_PRIMARY }}>
                {`${get(task, "project.contact.user.firstname").substring(
                  0,
                  1
                )} ${get(task, "project.contact.user.lastname").substring(
                  0,
                  1
                )}`}
              </Avatar>
              {`${get(task, "project.contact.user.firstname")} ${get(
                task,
                "project.contact.user.lastname"
              )}`}
            </div>
          </div> */}

          <div className="properties-section__row">
            <div className="properties-section__label">
              <MdCalendarToday />
              <span>{DETAIL.CREATED_ON}</span>
            </div>
            <div className="properties-section__value">
              {displayDate({ date: get(task, "createdDate") })}
            </div>
          </div>
        </div>
      </div>

      <div className="right-panel">
        <div className="contact-card">
          <div className="contact-card__header">
            <Avatar size={64} style={{ backgroundColor: COLORS.C_PRIMARY }}>
              {upperCase(
                `${
                  get(project, "contact.user.firstname")?.substring(0, 1) || ""
                } ${
                  get(project, "contact.user.lastname")?.substring(0, 1) || ""
                }`,
              )}
            </Avatar>

            <div className="contact-card__info">
              <h2>
                {`${get(project, "contact.user.firstname") || ""} ${
                  get(project, "contact.user.lastname") || ""
                }`}
              </h2>
              <div className="contact-card__details">
                <span>
                  {get(project, "contact.telecoms", [])?.find(
                    ({ system }) => system === CONTACT_POINT_SYSTEM.PHONE,
                  )?.value && <MdPhone />}
                  {
                    get(project, "contact.telecoms", [])?.find(
                      ({ system }) => system === CONTACT_POINT_SYSTEM.PHONE,
                    )?.value
                  }
                </span>
                <span>
                  {get(project, "contact.telecoms", [])?.find(
                    ({ system }) => system === CONTACT_POINT_SYSTEM.EMAIL,
                  )?.value && <MdEmail />}
                  {
                    get(project, "contact.telecoms", [])?.find(
                      ({ system }) => system === CONTACT_POINT_SYSTEM.EMAIL,
                    )?.value
                  }
                </span>
              </div>
            </div>
            <Button
              type="primary--link"
              icon={<MdBarChart />}
              onClick={() => setOpenContactCard(true)}
            >
              {DETAIL.DETAILS}
            </Button>
          </div>

          <div className="project-section">
            <div className="project-section__header">
              <span>
                <MdFolder /> {DETAIL.PROJECT}
              </span>
              <Button
                type="primary--link"
                icon={<MdArrowForward />}
                onClick={onNavigateProjectDetail}
              >
                {DETAIL.OPEN_PROJECT}
              </Button>
            </div>
            <div className="project-section__meta">
              {/* <span>#4874471221</span> */}
              <Tag color="blue">{DETAIL.HEALTH}</Tag>
              <span className="creation-date">
                {DETAIL.CREATED_ON_2}{" "}
                {displayDate({ date: get(task, "project.createdDate") })}
              </span>
            </div>
          </div>
        </div>

        {subscriptions.map(
          ({
            id,
            status,
            insurancePlan,
            project: subscriptionProject,
            additionalInfo,
          }) => {
            const item = find(
              get(subscriptionProject, "generalCosts"),
              (cost) =>
                get(cost, "insurancePlan.id") === get(insurancePlan, "id"),
            );
            const totalCost = get(
              get(subscriptionProject, "fields.upsells", {}),
              insurancePlan.id,
              [],
            )
              ?.reduce((acc, curr) => acc + curr.cost, get(item, "cost", 0))
              ?.toFixed(2);

            const upsells = get(
              subscriptionProject,
              `fields.upsells.${insurancePlan.id}`,
              [],
            );

            return (
              <AntCard key={id} className="subscription-card">
                <div className="subscription-card__header">
                  <div>
                    <h3>{get(insurancePlan, "name", "")}</h3>
                    <Tag>{DETAIL.SUBSCRIPTION_STATUSES[status]}</Tag>
                  </div>
                  <div className="subscription-card__pricing">
                    <span className="label">{DETAIL.PRICING}</span>
                    <div className="price">
                      {costToFrench(totalCost)}{" "}
                      {CURRENCY_SYMBOL[get(item, "currency", "DEFAULT")]}{" "}
                      {DETAIL.PER_MONTH}
                    </div>
                    <span className="subtext">
                      {DETAIL.PER_YEAR.replace(
                        "{amount}",
                        `${costToFrench(totalCost * 12)} ${
                          CURRENCY_SYMBOL[get(item, "currency", "DEFAULT")]
                        }`,
                      )}
                    </span>
                  </div>
                </div>
                <div className="subscription-card__content">
                  {upsells.map(({ insurancePlan: { name } }) => (
                    <p key={name}>{name}</p>
                  ))}
                  <div className="subscription-card__metadata">
                    <div>
                      <span className="label">{DETAIL.EFFECT_DATE}</span>
                      <div>
                        {moment(
                          get(subscriptionProject, "contract.issuanceDate"),
                        ).format(DATE_STRING_FORMAT)}
                      </div>
                    </div>
                    <div>
                      <span className="label">{DETAIL.ATTRIBUTED_TO}</span>
                      <div>
                        <Avatar
                          size="small"
                          style={{ backgroundColor: COLORS.C_PRIMARY }}
                        >
                          {upperCase(
                            `${
                              get(
                                subscriptionProject,
                                "author.firstname",
                              )?.substring(0, 1) || ""
                            } ${
                              get(
                                subscriptionProject,
                                "author.lastname",
                              )?.substring(0, 1) || ""
                            }`,
                          )}
                        </Avatar>
                        {`${
                          get(subscriptionProject, "author.firstname") || ""
                        } ${get(subscriptionProject, "author.lastname") || ""}`}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="subscription-card__actions">
                  <Button
                    block
                    disabled={taskStatus === TASK_STATUSES.COMPLETED}
                    type="primary"
                    icon={<MdLink />}
                    onClick={async () => {
                      const {
                        data: {
                          tracer: { source },
                        },
                      } = await getTracer({
                        variables: {
                          where: {
                            flow: {
                              process: {
                                theme:
                                  THEMES_PALETTE[
                                    get(
                                      subscriptionProject,
                                      "fields.processTemplate",
                                    )
                                  ]?.PROSPECT_PROCESS,
                              },
                            },
                          },
                        },
                      });
                      setOpenTerminate({
                        open: true,
                        state: STATES.GENERATE_LINK,
                        data: {
                          projectId: subscriptionProject?.id,
                          subscriptionId: id,
                          insurancePlanIds: [insurancePlan?.id],
                          isInfoCompleted: additionalInfo?.isInfoCompleted,
                          baseUrl: get(
                            insurancePlan,
                            "config.generatedUrl",
                            `${
                              process.env.REACT_APP_BASE_URL
                            }/#/public/process/init/${get(source, "id")}`,
                          ),
                        },
                      });
                    }}
                  >
                    {DETAIL.RELANCE_AND_CLOSE}
                  </Button>
                  <Button
                    block
                    disabled={taskStatus === TASK_STATUSES.COMPLETED}
                    type="secondary"
                    icon={<MdArrowForward />}
                    onClick={() =>
                      setOpenTerminate({ open: true, state: STATES.NAVIGATE })
                    }
                  >
                    {DETAIL.RESUME_AND_CLOSE}
                  </Button>
                </div>
              </AntCard>
            );
          },
        )}

        <div className="activity-section">
          <h3>
            <MdAccessTime /> {DETAIL.RECENT_ACTIVITIES}
          </h3>
          {userActivitiesLoading ? (
            <Spin />
          ) : isEmpty(userActivities) ? (
            <Empty />
          ) : (
            <Timeline
              items={userActivities?.map(
                ({
                  icon,
                  label,
                  clientFirstName,
                  clientLastName,
                  createdDate,
                }) => ({
                  dot: icon || <MdFlashOn className="icon-yellow" />,
                  children: (
                    <div className="timeline-item">
                      <div className="timeline-content">
                        <div>{label}</div>
                        <div className="timeline-user">
                          <Avatar
                            size="small"
                            style={{ backgroundColor: "#52c41a" }}
                          >
                            {upperCase(
                              `${clientFirstName.substring(
                                0,
                                1,
                              )}${clientLastName?.substring(0, 1)}`,
                            )}
                          </Avatar>
                          {clientFirstName} {clientLastName}
                        </div>
                      </div>
                      <span className="timeline-date">
                        {displayDate({ date: createdDate })}
                      </span>
                    </div>
                  ),
                }),
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetails;
